import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Plus, Calendar, Users, ChevronRight, UserPlus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../utils/cn';
import { Badge } from './ui/badge';

const Tournaments = ({ session, supabase }) => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPwa, setIsPwa] = useState(false);
  const [signedUpTournaments, setSignedUpTournaments] = useState({});

  useEffect(() => {
    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsPwa(true);
    }

    // Fetch tournaments
    const fetchTournaments = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('tournaments')
          .select(`
            id, 
            name,
            description,
            start_date,
            end_date,
            status,
            created_by,
            created_at,
            player_count,
            winner_id,
            open_registration,
            registration_closed
          `)
          .order('start_date', { ascending: false });

        if (error) throw error;

        // Process tournaments with additional info
        const processedTournaments = data || [];
        setTournaments(processedTournaments);
        
        // If user is logged in, check which tournaments they're signed up for
        if (session) {
          const { data: userPlayers } = await supabase
            .from('players')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('is_active', true);
            
          if (userPlayers && userPlayers.length > 0) {
            const playerId = userPlayers[0].id;
            
            const { data: tournamentPlayers } = await supabase
              .from('tournament_players')
              .select('tournament_id')
              .eq('player_id', playerId);
              
            if (tournamentPlayers) {
              const signedUpMap = {};
              tournamentPlayers.forEach(tp => {
                signedUpMap[tp.tournament_id] = true;
              });
              setSignedUpTournaments(signedUpMap);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching tournaments:', err.message);
        setError('Failed to load tournaments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, [supabase, session]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get tournament status label and color
  const getTournamentStatus = (status) => {
    switch (status) {
      case 'upcoming':
        return { label: 'Upcoming', color: 'text-primary bg-primary/10' };
      case 'active':
        return { label: 'Active', color: 'text-success bg-success/10' };
      case 'completed':
        return { label: 'Completed', color: 'text-muted-foreground bg-muted' };
      default:
        return { label: 'Unknown', color: 'text-muted-foreground bg-muted' };
    }
  };

  // Get registration status badge
  const getRegistrationStatus = (tournament) => {
    if (tournament.status !== 'upcoming') {
      return null;
    }
    
    if (tournament.registration_closed) {
      return { label: 'Registration Closed', color: 'bg-orange-100 text-orange-800' };
    }
    
    if (tournament.open_registration) {
      return { label: 'Open Registration', color: 'bg-green-100 text-green-800' };
    }
    
    return { label: 'Invite Only', color: 'bg-blue-100 text-blue-800' };
  };

  // Navigate to create tournament page
  const handleCreateTournament = () => {
    navigate('/tournament/create');
  };

  // Navigate to tournament details page
  const handleViewTournament = (id) => {
    navigate(`/tournament/${id}`);
  };

  // Quick sign-up for tournament without navigating to details page
  const handleQuickSignup = async (e, tournamentId) => {
    e.stopPropagation(); // Prevent navigating to tournament details
    
    if (!session) {
      navigate('/login');
      return;
    }
    
    try {
      // Find player associated with current user
      const { data: userPlayers } = await supabase
        .from('players')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('is_active', true);
      
      if (!userPlayers || userPlayers.length === 0) {
        setError('You need to create a player profile first');
        return;
      }
      
      const playerId = userPlayers[0].id;
      
      // Add player to tournament
      const { error } = await supabase
        .from('tournament_players')
        .insert([{
          tournament_id: tournamentId,
          player_id: playerId
        }]);
      
      if (error) throw error;
      
      // Update tournament player count
      const tournamentToUpdate = tournaments.find(t => t.id === tournamentId);
      await supabase
        .from('tournaments')
        .update({
          player_count: tournamentToUpdate.player_count + 1
        })
        .eq('id', tournamentId);
      
      // Update local state
      setSignedUpTournaments(prev => ({
        ...prev,
        [tournamentId]: true
      }));
      
      setTournaments(tournaments.map(t => 
        t.id === tournamentId 
          ? {...t, player_count: t.player_count + 1} 
          : t
      ));
    } catch (err) {
      console.error('Error signing up for tournament:', err.message);
      setError('Failed to sign up for tournament');
    }
  };

  // Withdraw from tournament without navigating to details page
  const handleWithdraw = async (e, tournamentId) => {
    e.stopPropagation(); // Prevent navigating to tournament details
    
    if (!session) {
      navigate('/login');
      return;
    }
    
    try {
      // Find player associated with current user
      const { data: userPlayers } = await supabase
        .from('players')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('is_active', true);
      
      if (!userPlayers || userPlayers.length === 0) {
        setError('Player profile not found');
        return;
      }
      
      const playerId = userPlayers[0].id;
      
      // Remove player from tournament
      const { error } = await supabase
        .from('tournament_players')
        .delete()
        .eq('tournament_id', tournamentId)
        .eq('player_id', playerId);
      
      if (error) throw error;
      
      // Update tournament player count
      const tournamentToUpdate = tournaments.find(t => t.id === tournamentId);
      if (tournamentToUpdate.player_count > 0) {
        await supabase
          .from('tournaments')
          .update({
            player_count: tournamentToUpdate.player_count - 1
          })
          .eq('id', tournamentId);
      }
      
      // Update local state
      const updatedSignups = {...signedUpTournaments};
      delete updatedSignups[tournamentId];
      setSignedUpTournaments(updatedSignups);
      
      setTournaments(tournaments.map(t => 
        t.id === tournamentId 
          ? {...t, player_count: Math.max(0, t.player_count - 1)} 
          : t
      ));
    } catch (err) {
      console.error('Error withdrawing from tournament:', err.message);
      setError('Failed to withdraw from tournament');
    }
  };

  return (
    <div className={cn(
      "flex flex-col w-full p-4 md:p-6 gap-6 pb-20",
      isPwa && "pt-12" // Add padding for PWA status bar
    )}>
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Tournaments</h1>
        {session && (
          <Button onClick={handleCreateTournament} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Tournament
          </Button>
        )}
      </div>

      {error && (
        <div className="text-red-500 p-4 bg-red-50 rounded-md">{error}</div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading tournaments...</p>
        </div>
      ) : error ? (
        <div className="text-red-500 p-4 bg-red-50 rounded-md">{error}</div>
      ) : tournaments.length === 0 ? (
        <div className="text-center p-8 bg-muted rounded-lg">
          <Trophy className="w-12 h-12 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No tournaments yet</h3>
          <p className="mt-2 text-muted-foreground">
            Be the first to create a tournament and invite players to compete!
          </p>
          {session && (
            <Button onClick={handleCreateTournament} className="mt-4">
              Create Tournament
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tournaments.map((tournament) => {
            const { label: statusLabel, color: statusColor } = getTournamentStatus(tournament.status);
            const registrationStatus = getRegistrationStatus(tournament);
            const isSignedUp = signedUpTournaments[tournament.id];
            const canSignUp = tournament.status === 'upcoming' && 
                             !tournament.registration_closed && 
                             tournament.open_registration && 
                             !isSignedUp &&
                             session;
            
            return (
              <Card 
                key={tournament.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewTournament(tournament.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{tournament.name}</CardTitle>
                    <span className={cn("px-2 py-1 rounded-full text-xs font-medium", statusColor)}>
                      {statusLabel}
                    </span>
                  </div>
                  <CardDescription className="line-clamp-2">{tournament.description}</CardDescription>
                  
                  {/* Registration Status Badge */}
                  {registrationStatus && (
                    <div className="mt-2">
                      <Badge variant="outline" className={cn(registrationStatus.color, "text-xs")}>
                        {registrationStatus.label}
                      </Badge>
                      
                      {isSignedUp && (
                        <Badge variant="outline" className="ml-2 bg-blue-100 text-blue-800 text-xs">
                          You're Registered
                        </Badge>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {formatDate(tournament.start_date)}
                      {tournament.end_date && ` - ${formatDate(tournament.end_date)}`}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      {tournament.player_count || 0} players
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-1">
                  <div className="flex justify-between items-center w-full">
                    {tournament.winner_id ? (
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-medium">Winner Declared</span>
                      </div>
                    ) : canSignUp ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex items-center gap-1"
                        onClick={(e) => handleQuickSignup(e, tournament.id)}
                      >
                        <UserPlus className="w-3 h-3" />
                        Sign Up
                      </Button>
                    ) : isSignedUp && tournament.status === 'upcoming' && !tournament.registration_closed ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex items-center gap-1 border-red-200 hover:bg-red-50 text-red-600"
                        onClick={(e) => handleWithdraw(e, tournament.id)}
                      >
                        <X className="w-3 h-3" />
                        Withdraw
                      </Button>
                    ) : (
                      <div className="flex-1"></div>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Tournaments; 